/** Add or edit a success case: named fields, the anonymized label and aliases, metrics, and a live preview. */

import { useMemo, useState } from 'react';
import { Globe, Info } from 'lucide-react';

import { useSaveCase } from '@/api/queries/cases';
import type { CaseOut } from '@/api/types/cases';
import { bannerError, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState, type ButtonState } from '@/ui/ActionButton';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { AnonymizedPreview } from './AnonymizedPreview';
import { FIELD_PATHS, IdentityFields, TextFields, type SetField } from './CaseFields';
import { MetricsEditor } from './MetricsEditor';
import { caseBody, caseErrors, caseFormFrom, changedFields, emptyCaseForm } from './caseForm';


function SharedNotice({ item }: { item?: CaseOut }) {
  if (!item?.shareable) return null;
  return (
    <div className="alert alert-info" role="note">
      <Info aria-hidden="true" />
      <div className="a-body">
        This case is shared with clients. Saving a change to its text, metrics, client name or aliases makes it internal
        again until you review the anonymized version and confirm it.
      </div>
    </div>
  );
}

function LinkedAssets({ item, assetName }: { item?: CaseOut; assetName: string }) {
  const names = item ? item.assets.map((a) => a.name) : [assetName];
  return (
    <div className="field">
      <span className="label">Assets linked</span>
      <p className="hint">{names.length > 0 ? names.join(', ') : 'None'}. Link or unlink it from each asset&apos;s Success cases tab.</p>
    </div>
  );
}

function EditorFooter({
  item,
  dirty,
  blocked,
  state,
  onClose,
  onShare,
}: {
  item?: CaseOut;
  dirty: boolean;
  blocked: boolean;
  state: ButtonState;
  onClose: () => void;
  onShare: (item: CaseOut) => void;
}) {
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
      {item && !item.shareable && dirty && (
        <span id="case-share-note" className="hint modal-foot-start">Save your changes to share with clients.</span>
      )}
      {item && !item.shareable && (
        <button type="button" className="btn btn-secondary" disabled={dirty} aria-describedby={dirty ? 'case-share-note' : undefined} onClick={() => onShare(item)}>
          <Globe aria-hidden="true" /> Share with clients
        </button>
      )}
      <ActionButton state={state} label={item ? 'Save case' : 'Add case'} disabled={blocked || (item !== undefined && !dirty)} />
    </>
  );
}

export function CaseEditor({
  asset,
  item,
  onClose,
  onShare,
}: {
  asset: { id: string; name: string };
  item?: CaseOut;
  onClose: () => void;
  onShare: (item: CaseOut) => void;
}) {
  const save = useSaveCase();
  const initial = useMemo(() => (item ? caseFormFrom(item) : emptyCaseForm()), [item]);
  const [form, setForm] = useState(initial);
  const set: SetField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const dirty = JSON.stringify(caseBody(initial)) !== JSON.stringify(caseBody(form));
  const errors = caseErrors(form);
  const blocked = Object.keys(errors).length > 0;
  // The label's problem reads as the hint below the form; the others sit beside their fields, with the server's.
  const { anonymized_label: problem, ...clientErrors } = errors;
  const shown = { ...clientErrors, ...serverFieldErrors(save.error) };

  function submit() {
    if (blocked) return;
    const change = item ? changedFields(initial, form) : { ...caseBody(form), asset_ids: [asset.id] };
    save.mutate({ id: item?.id, change }, { onSuccess: onClose });
  }

  return (
    <Modal title={item ? `Edit ${item.anonymized_label}` : 'Add success case'} size="xwide" onClose={onClose} onSubmit={submit} footer={<EditorFooter item={item} dirty={dirty} blocked={blocked} state={buttonState(save)} onClose={onClose} onShare={onShare} />}>
      <div className="case-editor">
        <div className="stack-md">
          <SharedNotice item={item} />
          <FormError error={bannerError(save.error, FIELD_PATHS)} />
          <IdentityFields form={form} set={set} errors={shown} />
          <TextFields form={form} set={set} errors={shown} />
          <MetricsEditor rows={form.metrics} onChange={(rows) => set('metrics', rows)} errors={clientErrors} />
          <LinkedAssets item={item} assetName={asset.name} />
          {problem && <p className="hint">{problem}</p>}
        </div>
        <AnonymizedPreview caseId={item?.id ?? null} dirty={dirty} form={form} />
      </div>
    </Modal>
  );
}
